/**
 * Business view or Accountant view: the words, columns and density of the
 * purchases screens, per person and per company. Kept in this browser only.
 */
import { useSyncExternalStore } from "react";
import type { components } from "@linkhr/api-client/core";
import { useAuth } from "@linkhr/auth/react";
import { useThemeDensity } from "@linkhr/design-system/runtime";
import { CODES, codesOf } from "../ledger/books";
import { useLedgerAccess } from "../ledger/useLedgerAccess";
import { ownsCompany } from "./gate";

export type ViewMode = "business" | "accountant";

/** Bookkeeping work: a grant holding any of these starts in Accountant view. */
const ACCOUNTANT_CODES: readonly string[] = [
  CODES.journal,
  CODES.bankImport,
  CODES.bankReconcile,
  CODES.audit,
];

/** The view a person starts in: the owner sees the business, a bookkeeper the books. */
export function defaultViewMode(company: components["schemas"]["CapabilityCompany"]): ViewMode {
  if (ownsCompany(company)) return "business";
  return codesOf(company).some((code) => ACCOUNTANT_CODES.includes(code))
    ? "accountant"
    : "business";
}

/** `{userId}:{companyId}`: two people signing in on one browser keep their own views. */
const storageKey = (key: string): string => `linkhr:acct:view:${key}`;
// This session's picks, so a switch holds where storage is blocked (a private window).
const picked = new Map<string, ViewMode>();
const listeners = new Set<() => void>();

function read(key: string): ViewMode | null {
  const mode = picked.get(key);
  if (mode !== undefined) return mode;
  try {
    const stored = window.localStorage.getItem(storageKey(key));
    return stored === "business" || stored === "accountant" ? stored : null;
  } catch {
    return null;
  }
}

function write(key: string, mode: ViewMode): void {
  picked.set(key, mode);
  try {
    window.localStorage.setItem(storageKey(key), mode);
  } catch {
    // Storage blocked: the pick lasts this session.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useViewMode(companyId: string): {
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
} {
  const { company } = useLedgerAccess(companyId);
  const { userId } = useAuth();
  const { setDensity } = useThemeDensity();
  const key = `${userId ?? ""}:${companyId}`;
  const chosen = useSyncExternalStore(subscribe, () => read(key));
  return {
    mode: chosen ?? (company === undefined ? "business" : defaultViewMode(company)),
    setMode: (mode) => {
      write(key, mode);
      // Each switch applies its view's density once; the menu's density toggle still overrides it.
      setDensity(mode === "accountant" ? "compact" : "comfortable");
    },
  };
}
